import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DashboardSidebar from '../../components/ui/DashboardSidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import SubscriptionRequiredModal from '../../components/ui/SubscriptionRequiredModal';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { isSubscriptionError } from '../../lib/subscriptionGate';
import LibraryCover from './components/LibraryCover';
import StarRating from './components/StarRating';

type ClassroomLite = { id: number; name: string; is_admin: boolean };

type LibraryItem = {
  id: number;
  title: string;
  description: string | null;
  education_level: string | null;
  category: string | null;
  visibility: 'class' | 'public' | string;
  status: 'pending' | 'approved' | 'rejected' | string;
  classroom_id: number | null;
  file_url: string;
  original_name: string;
  size_bytes: number | null;
  download_count: number;
  rating_avg?: number;
  rating_count?: number;
  my_rating?: number | null;
  created_at: string | null;
  created_by_id?: number;
};

type TabKey = 'public' | 'class' | 'pending' | 'upload';

const levelOptions = [
  { value: '', label: 'Toate nivelurile' },
  { value: 'primar', label: 'Primar' },
  { value: 'gimnazial', label: 'Gimnazial' },
  { value: 'liceal', label: 'Liceal' },
  { value: 'universitar', label: 'Universitar' },
];

const categoryOptions = [
  { value: '', label: 'Toate categoriile' },
  { value: 'istorie', label: 'Istorie' },
  { value: 'literatura', label: 'Literatură' },
  { value: 'stiinte', label: 'Științe' },
  { value: 'matematica', label: 'Matematică' },
  { value: 'informatica', label: 'Informatică' },
  { value: 'arte', label: 'Arte' },
  { value: 'diverse', label: 'Diverse' },
];

export default function LibraryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { me, uiRole, signOut } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const user = useMemo(() => {
    return {
      role: (uiRole || 'student') as 'admin' | 'teacher' | 'student' | 'parent',
      name: me?.full_name || 'Utilizator',
      avatar: me?.avatar_url || undefined,
    };
  }, [me?.avatar_url, me?.full_name, uiRole]);

  const showSidebar = user.role === 'teacher' || user.role === 'admin';
  const isPlatformAdmin = useMemo(() => Boolean((me as any)?.is_platform_admin) && user.role === 'admin', [me, user.role]);

  const [tab, setTab] = useState<TabKey>('public');
  const [classrooms, setClassrooms] = useState<ClassroomLite[]>([]);
  const [selectedClassroomId, setSelectedClassroomId] = useState<number | null>(null);

  const [q, setQ] = useState('');
  const [educationLevel, setEducationLevel] = useState('');
  const [category, setCategory] = useState('');

  const [items, setItems] = useState<LibraryItem[]>([]);
  const [pending, setPending] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingPending, setLoadingPending] = useState(false);

  const canModerate = useMemo(() => {
    const c = classrooms.find((x) => x.id === selectedClassroomId);
    return !!c?.is_admin;
  }, [classrooms, selectedClassroomId]);

  const classroomOptions = useMemo(
    () => classrooms.map((c) => ({ value: c.id, label: c.name })),
    [classrooms]
  );

  const loadClassrooms = useCallback(async () => {
    try {
      const resp = await api.get('/api/biblioteca/my-classrooms');
      const list: ClassroomLite[] = Array.isArray(resp.data) ? resp.data : [];
      setClassrooms(list);
      if (!selectedClassroomId && list.length > 0) {
        setSelectedClassroomId(list[0].id);
      }
    } catch {
      setClassrooms([]);
    }
  }, [selectedClassroomId]);

  const loadPublic = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get('/api/biblioteca/public', {
        params: {
          q: q || undefined,
          education_level: educationLevel || undefined,
          category: category || undefined,
        },
      });
      setItems(Array.isArray(resp.data) ? resp.data : []);
    } finally {
      setLoading(false);
    }
  }, [category, educationLevel, q]);

  const loadClassroom = useCallback(async () => {
    if (!selectedClassroomId) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const resp = await api.get(`/api/biblioteca/classroom/${selectedClassroomId}`, {
        params: { q: q || undefined },
      });
      setItems(Array.isArray(resp.data) ? resp.data : []);
    } finally {
      setLoading(false);
    }
  }, [q, selectedClassroomId]);

  const loadPending = useCallback(async () => {
    if (!selectedClassroomId || !canModerate) {
      setPending([]);
      return;
    }
    setLoadingPending(true);
    try {
      const resp = await api.get(`/api/biblioteca/classroom/${selectedClassroomId}/pending`);
      setPending(Array.isArray(resp.data) ? resp.data : []);
    } finally {
      setLoadingPending(false);
    }
  }, [canModerate, selectedClassroomId]);

  useEffect(() => {
    void loadClassrooms();
  }, [loadClassrooms]);

  useEffect(() => {
    if (tab === 'public') void loadPublic();
    if (tab === 'class') void loadClassroom();
  }, [loadClassroom, loadPublic, tab]);

  useEffect(() => {
    if (tab === 'pending') void loadPending();
  }, [loadPending, tab]);

  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadLevel, setUploadLevel] = useState('');
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadVisibility, setUploadVisibility] = useState<'class' | 'public'>('class');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const visibilityOptions = useMemo(() => {
    const base = [{ value: 'class', label: 'Doar pentru clasă' }];
    if (user.role === 'teacher' || user.role === 'admin') {
      base.push({ value: 'public', label: 'Public (platformă)' });
    }
    return base;
  }, [user.role]);

  const DOWNLOAD_PROMPT_KEY = 'educonnect_library_download_prompt_dismissed';
  const [downloadPrompt, setDownloadPrompt] = useState<{ id: number } | null>(null);
  const [dontShowDownloadPromptAgain, setDontShowDownloadPromptAgain] = useState(false);

  const doDownload = async (id: number) => {
    try {
      const resp = await api.get(`/api/biblioteca/resources/${id}/download`);
      const urlPath = String((resp.data as any)?.url || '');
      const base = String((api.defaults.baseURL || window.location.origin) as any).replace(/\/$/, '');
      const fullUrl = urlPath ? `${base}${urlPath.startsWith('/') ? urlPath : `/${urlPath}`}` : '';
      if (fullUrl) {
        window.open(fullUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (e: any) {
      if (isSubscriptionError(e)) {
        setUpgradeOpen(true);
        return;
      }
      alert(e?.response?.data?.detail || 'Nu am putut descărca resursa.');
    }
  };

  const handleDownload = (id: number) => {
    const dismissed = window.localStorage.getItem(DOWNLOAD_PROMPT_KEY) === '1';
    if (dismissed) {
      void doDownload(id);
      return;
    }
    setDontShowDownloadPromptAgain(false);
    setDownloadPrompt({ id });
  };

  const handleRate = async (id: number, stars: number) => {
    try {
      const form = new FormData();
      form.append('stars', String(stars));
      const resp = await api.post(`/api/biblioteca/resources/${id}/rate`, form);
      const avg = Number((resp.data as any)?.rating_avg ?? 0);
      const cnt = Number((resp.data as any)?.rating_count ?? 0);
      const my = Number((resp.data as any)?.my_rating ?? stars);

      setItems((prev) =>
        prev.map((it) =>
          it.id === id
            ? { ...it, rating_avg: avg, rating_count: cnt, my_rating: my }
            : it
        )
      );
      setPending((prev) =>
        prev.map((it) =>
          it.id === id
            ? { ...it, rating_avg: avg, rating_count: cnt, my_rating: my }
            : it
        )
      );
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut salva votul.');
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await api.post(`/api/biblioteca/resources/${id}/approve`);
      await loadPending();
      if (tab === 'class') await loadClassroom();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut aproba resursa.');
    }
  };

  const handleReject = async (id: number) => {
    const reason = window.prompt('Motiv respingere (opțional):', '') || '';
    try {
      const form = new FormData();
      form.append('reason', reason);
      await api.post(`/api/biblioteca/resources/${id}/reject`, form);
      await loadPending();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut respinge resursa.');
    }
  };

  const handleDeleteResource = async (id: number) => {
    if (!isPlatformAdmin) return;
    const ok = window.confirm('Ștergi această resursă din bibliotecă?');
    if (!ok) return;

    try {
      await api.delete(`/api/biblioteca/resources/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      setPending((prev) => prev.filter((x) => x.id !== id));
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut șterge resursa.');
    }
  };

  const handleUpload = async () => {
    if (!uploadTitle.trim()) {
      alert('Titlul este obligatoriu.');
      return;
    }
    if (!uploadFile) {
      alert('Selectează un fișier.');
      return;
    }
    if (uploadVisibility === 'class' && !selectedClassroomId) {
      alert('Selectează o clasă.');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('title', uploadTitle.trim());
      form.append('description', uploadDescription.trim());
      form.append('education_level', uploadLevel);
      form.append('category', uploadCategory);
      form.append('visibility', uploadVisibility);
      if (uploadVisibility === 'class' && selectedClassroomId) {
        form.append('classroom_id', String(selectedClassroomId));
      }
      form.append('file', uploadFile);

      const resp = await api.post('/api/biblioteca/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const status = String((resp.data as any)?.status || '');
      if (status === 'pending') {
        alert('Resursa a fost trimisă spre aprobare.');
      } else {
        alert('Resursa a fost publicată.');
      }

      setUploadTitle('');
      setUploadDescription('');
      setUploadFile(null);
      setUploadLevel('');
      setUploadCategory('');
      setUploadVisibility('class');
      setTab(uploadVisibility === 'public' ? 'public' : 'class');
      if (uploadVisibility === 'public') await loadPublic();
      else await loadClassroom();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut încărca resursa.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Bibliotecă - Scola</title>
      </Helmet>

      <Header user={user} onNavigate={(p) => navigate(p)} onSignOut={() => signOut()} />

      <div className="min-h-screen bg-background pt-16">
        {downloadPrompt ? (
          <>
            <div
              className="fixed inset-0 bg-black/40 z-[2000]"
              onClick={() => setDownloadPrompt(null)}
              aria-hidden="true"
            />
            <div className="fixed inset-0 z-[2010] flex items-center justify-center p-4">
              <div className="w-full max-w-lg rounded-2xl border border-border bg-card shadow-lg overflow-hidden">
                <div className="p-5 border-b border-border">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="Library" size={18} className="text-primary" />
                      </div>
                      <div>
                        <div className="text-base font-semibold text-foreground">Ajută-ne să umplem biblioteca</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Comunitatea crește când fiecare contribuie cu ceva util.
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDownloadPrompt(null)}
                      className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                      aria-label="Închide"
                    >
                      <Icon name="X" size={18} />
                    </button>
                  </div>
                </div>

                <div className="p-5 space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Dacă ai o carte, proiect, lucrare sau material util, încarcă-l și tu în Bibliotecă. Resursele
                    încărcate de elevi și părinți pentru clasă vor fi aprobate de profesorul administrator al clasei.
                  </p>
                  <div className="rounded-lg bg-muted/40 border border-border p-3">
                    <div className="flex items-start gap-2">
                      <Icon name="Info" size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-muted-foreground">
                        Te rugăm să încarci doar materiale pentru care ai drept de distribuire.
                      </div>
                    </div>
                  </div>

                  <Checkbox
                    label="Nu mai afișa acest mesaj"
                    checked={dontShowDownloadPromptAgain}
                    onChange={(e) => setDontShowDownloadPromptAgain(Boolean(e.target.checked))}
                  />
                </div>

                <div className="p-5 border-t border-border flex flex-col sm:flex-row gap-2 sm:justify-end">
                  <Button
                    variant="outline"
                    iconName="Upload"
                    onClick={() => {
                      if (dontShowDownloadPromptAgain) {
                        window.localStorage.setItem(DOWNLOAD_PROMPT_KEY, '1');
                      }
                      setDownloadPrompt(null);
                      setTab('upload');
                    }}
                  >
                    Încarcă o resursă
                  </Button>
                  <Button
                    variant="default"
                    iconName="Download"
                    iconPosition="right"
                    onClick={async () => {
                      if (dontShowDownloadPromptAgain) {
                        window.localStorage.setItem(DOWNLOAD_PROMPT_KEY, '1');
                      }
                      const id = downloadPrompt.id;
                      setDownloadPrompt(null);
                      await doDownload(id);
                    }}
                  >
                    Descarcă acum
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : null}

        {showSidebar ? (
          <DashboardSidebar
            user={{ role: user.role }}
            isCollapsed={isSidebarCollapsed}
            onNavigate={(path) => navigate(path)}
          />
        ) : null}

        <main className={`transition-all duration-300 ${showSidebar ? (isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64') : ''}`}>
          <div className="p-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
              <div className="flex items-center gap-3">
                {showSidebar ? (
                  <button
                    onClick={() => setIsSidebarCollapsed((p) => !p)}
                    className="hidden lg:flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted transition-colors"
                    aria-label="Comută bara laterală"
                  >
                    <Icon name={isSidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={20} />
                  </button>
                ) : null}
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Bibliotecă</h1>
                  <p className="text-sm text-muted-foreground">
                    Cărți, proiecte și resurse. Elevii și părinții trimit resursele clasei spre aprobare.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="default" iconName="Upload" onClick={() => setTab('upload')}>
                  Încarcă
                </Button>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between mb-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setTab('public')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
                    tab === 'public'
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card border-border text-foreground'
                  }`}
                >
                  Public
                </button>
                <button
                  onClick={() => setTab('class')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
                    tab === 'class'
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card border-border text-foreground'
                  }`}
                >
                  Clasă
                </button>
                <button
                  onClick={() => setTab('pending')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
                    tab === 'pending'
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card border-border text-foreground'
                  }`}
                  disabled={!canModerate}
                  title={!canModerate ? 'Disponibil doar pentru adminul clasei' : undefined}
                >
                  În așteptare
                </button>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                {classrooms.length > 0 ? (
                  <div className="min-w-[260px]">
                    <Select
                      label="Clasă"
                      value={selectedClassroomId ?? ''}
                      options={classroomOptions}
                      onChange={(v) => {
                        const id = Number(v);
                        setSelectedClassroomId(Number.isFinite(id) ? id : null);
                      }}
                      searchable
                      clearable
                    />
                  </div>
                ) : null}
              </div>
            </div>

            {tab === 'upload' ? (
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold text-foreground mb-1">Încarcă o resursă</h2>
                <p className="text-sm text-muted-foreground mb-5">
                  Elevii și părinții pot încărca resurse în biblioteca clasei (cu aprobare). Profesorii pot publica și
                  resurse publice.
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Input
                    label="Titlu"
                    placeholder="Ex: Eseu - Unirea Principatelor"
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    required
                    disabled={uploading}
                  />
                  <Select
                    label="Vizibilitate"
                    value={uploadVisibility}
                    options={visibilityOptions}
                    onChange={(v) => setUploadVisibility(String(v) as any)}
                    disabled={uploading}
                  />

                  <Select
                    label="Nivel învățământ"
                    value={uploadLevel}
                    options={levelOptions.filter((o) => o.value !== '')}
                    onChange={(v) => setUploadLevel(String(v))}
                    disabled={uploading}
                    searchable
                    clearable
                  />
                  <Select
                    label="Categorie"
                    value={uploadCategory}
                    options={categoryOptions.filter((o) => o.value !== '')}
                    onChange={(v) => setUploadCategory(String(v))}
                    disabled={uploading}
                    searchable
                    clearable
                  />
                </div>

                <div className="mt-4">
                  <Input
                    label="Descriere (opțional)"
                    placeholder="Scurtă descriere..."
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    disabled={uploading}
                  />
                </div>

                <div className="mt-4">
                  <label className="text-sm font-medium leading-none mb-2 block text-foreground">
                    Fișier <span className="text-destructive ml-1">*</span>
                  </label>
                  <input
                    type="file"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    disabled={uploading}
                    className="block w-full text-sm"
                  />
                  {uploadFile ? (
                    <div className="mt-2 text-xs text-muted-foreground">
                      Selectat: <span className="text-foreground">{uploadFile.name}</span>
                    </div>
                  ) : null}
                </div>

                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <Button onClick={handleUpload} loading={uploading} iconName="Upload" iconPosition="right">
                    Încarcă resursa
                  </Button>
                  <Button variant="outline" onClick={() => setTab('public')} disabled={uploading}>
                    Înapoi
                  </Button>
                </div>
              </div>
            ) : null}

            {tab !== 'upload' ? (
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  <Input
                    label="Caută"
                    placeholder="Titlu..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                  {tab === 'public' ? (
                    <>
                      <Select
                        label="Nivel"
                        value={educationLevel}
                        options={levelOptions}
                        onChange={(v) => setEducationLevel(String(v))}
                        searchable
                        clearable
                      />
                      <Select
                        label="Categorie"
                        value={category}
                        options={categoryOptions}
                        onChange={(v) => setCategory(String(v))}
                        searchable
                        clearable
                      />
                    </>
                  ) : (
                    <div className="flex items-end">
                      <Button variant="outline" iconName="RefreshCw" onClick={() => (tab === 'class' ? loadClassroom() : loadPublic())}>
                        Reîncarcă
                      </Button>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex gap-2 flex-wrap">
                  <Button
                    variant="default"
                    iconName="Search"
                    onClick={() => (tab === 'class' ? loadClassroom() : loadPublic())}
                    disabled={loading}
                  >
                    Caută
                  </Button>
                  <Button
                    variant="outline"
                    iconName="X"
                    onClick={() => {
                      setQ('');
                      setEducationLevel('');
                      setCategory('');
                      if (tab === 'class') void loadClassroom();
                      else void loadPublic();
                    }}
                    disabled={loading}
                  >
                    Resetează
                  </Button>
                </div>
              </div>
            ) : null}

            {tab === 'pending' ? (
              <div className="mt-6 bg-card border border-border rounded-lg overflow-hidden">
                {!canModerate ? (
                  <div className="p-6 text-sm text-muted-foreground">
                    Doar administratorul clasei poate vedea și aproba resursele în așteptare.
                    <div className="mt-3">
                      <Button variant="outline" onClick={() => setTab('class')}>
                        Vezi biblioteca clasei
                      </Button>
                    </div>
                  </div>
                ) : loadingPending ? (
                  <div className="p-6 text-sm text-muted-foreground">Se încarcă...</div>
                ) : pending.length === 0 ? (
                  <div className="p-10 flex flex-col items-center justify-center">
                    <Icon name="Inbox" size={44} className="text-muted-foreground mb-3" />
                    <div className="text-sm text-muted-foreground text-center">Nu există resurse în așteptare.</div>
                  </div>
                ) : (
                  <ul className="divide-y divide-border">
                    {pending.map((r) => (
                      <li key={r.id} className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-foreground truncate">{r.title}</div>
                            {r.description ? (
                              <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{r.description}</div>
                            ) : null}
                            <div className="mt-2 text-xs text-muted-foreground flex flex-wrap gap-2">
                              <span className="inline-flex items-center gap-1">
                                <Icon name="File" size={14} />
                                {r.original_name}
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <Icon name="Clock" size={14} />
                                {r.created_at ? new Date(r.created_at).toLocaleString() : '-'}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <Button variant="outline" iconName="Download" onClick={() => handleDownload(r.id)}>
                              Vezi
                            </Button>
                            <Button variant="default" iconName="Check" onClick={() => handleApprove(r.id)}>
                              Aprobă
                            </Button>
                            <Button variant="destructive" iconName="X" onClick={() => handleReject(r.id)}>
                              Respinge
                            </Button>
                            {isPlatformAdmin ? (
                              <Button variant="destructive" iconName="Trash2" onClick={() => void handleDeleteResource(r.id)}>
                                Șterge
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}

            {tab === 'public' || tab === 'class' ? (
              <div className="mt-6 bg-card border border-border rounded-lg overflow-hidden">
                {loading ? (
                  <div className="p-6 text-sm text-muted-foreground">Se încarcă...</div>
                ) : items.length === 0 ? (
                  <div className="p-10 flex flex-col items-center justify-center">
                    <Icon name="Library" size={44} className="text-muted-foreground mb-3" />
                    <div className="text-sm text-muted-foreground text-center">Nu există resurse.</div>
                    <div className="mt-3">
                      <Button variant="outline" iconName="Upload" onClick={() => setTab('upload')}>
                        Încarcă o resursă
                      </Button>
                    </div>
                  </div>
                ) : (
                  <ul className="divide-y divide-border">
                    {items.map((r) => (
                      <li key={r.id} className="p-4">
                        <div className="flex items-start gap-4">
                          <LibraryCover
                            title={r.title}
                            category={r.category}
                            originalName={r.original_name}
                            fileUrl={r.file_url}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-foreground break-words line-clamp-2 sm:line-clamp-1">{r.title}</div>
                                {r.description ? (
                                  <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{r.description}</div>
                                ) : null}
                                <div className="mt-2 text-xs text-muted-foreground flex flex-wrap gap-2">
                                  <span className="inline-flex items-center gap-1">
                                    <Icon name="File" size={14} />
                                    {r.original_name}
                                  </span>
                                  {r.education_level ? (
                                    <span className="inline-flex items-center gap-1">
                                      <Icon name="GraduationCap" size={14} />
                                      {r.education_level}
                                    </span>
                                  ) : null}
                                  {r.category ? (
                                    <span className="inline-flex items-center gap-1">
                                      <Icon name="Tag" size={14} />
                                      {r.category}
                                    </span>
                                  ) : null}
                                  <span className="inline-flex items-center gap-1">
                                    <Icon name="ArrowDownToLine" size={14} />
                                    {r.download_count}
                                  </span>
                                </div>

                                <div className="mt-3">
                                  <StarRating
                                    value={r.my_rating ?? null}
                                    average={Number(r.rating_avg ?? 0)}
                                    count={Number(r.rating_count ?? 0)}
                                    onRate={(stars) => void handleRate(r.id, stars)}
                                  />
                                </div>
                              </div>

                              <div className="w-full sm:w-auto flex justify-end sm:justify-start gap-2">
                                <Button variant="outline" iconName="Download" onClick={() => handleDownload(r.id)}>
                                  Descarcă
                                </Button>
                                {isPlatformAdmin ? (
                                  <Button variant="destructive" iconName="Trash2" onClick={() => void handleDeleteResource(r.id)}>
                                    Șterge
                                  </Button>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}

            <div className="mt-10 text-xs text-muted-foreground">
              <span>
                Sugestie: încarcă doar materiale pentru care ai drept de distribuire. Vezi{' '}
                <Link
                  className="text-primary hover:underline"
                  to="/legal/termeni-si-conditii"
                  state={{ backgroundLocation: location }}
                >
                  Termenii și condițiile
                </Link>
                .
              </span>
            </div>
          </div>
        </main>

        <SubscriptionRequiredModal open={upgradeOpen} feature="downloads" onClose={() => setUpgradeOpen(false)} />
      </div>
    </>
  );
}
